/*
  lettoria-collection-tabs.js — grouped product tabs for a collection page.

  Ported from the HC "Collection Type Tabs" section (afroyla/skullette:
  assets/hc-collection-type-tabs.js). The grouping rules are carried over
  unchanged, because they encode how the merchandising team actually works:

    · merge map matches on a normalized key, so a rule written
      "Black Interior - Leather Handbag" still catches a product type saved
      as "Black Interior -Leather Handbag";
    · tab ORDER follows the order products appear in the collection, not the
      order of the lines in the textarea — the collection sort in Shopify is
      the reorder handle;
    · a stage with no products never becomes a tab, which is how campaign
      stages are scheduled: flip products to draft and their tab disappears.

  What the Horizon port drops: the old file fetched an invisible fragment
  section page by page, firing eight requests at a time and watching for the
  first short page to guess where the collection ended (MAX_PAGES = 25 as a
  backstop). None of that survives here — the section renders its own first
  page server-side and prints paginate.pages, so we ask for exactly the pages
  that exist, through the theme's own cached section renderer.
*/
import { sectionRenderer } from '@theme/section-renderer';

/** Sentinel group id for the "All" tab. */
const ALL = '__all__';

/** Pages fetched at once when a collection runs past the first 50 products. */
const FETCH_CONCURRENCY = 6;

/**
 * Canonicalizes a type or tag for fuzzy comparison: unifies en/em dashes and
 * the minus sign, normalizes spacing around dashes, collapses runs of
 * whitespace, lowercases.
 * @param {string} value
 * @returns {string}
 */
function normalizeKey(value) {
  return String(value ?? '')
    .replace(/[–—−]/g, '-')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Parses a "left = right" textarea, one rule per line. Lines without an `=`,
 * or with an empty side, are skipped rather than treated as errors — these
 * are merchant-typed settings and a stray blank line is normal.
 * @param {string | undefined} raw
 * @returns {Array<[string, string]>}
 */
function parseRules(raw) {
  if (!raw) return [];

  const rules = [];

  for (const line of raw.split(/\r?\n/)) {
    const separator = line.indexOf('=');
    if (separator <= 0) continue;

    const left = line.slice(0, separator).trim();
    const right = line.slice(separator + 1).trim();
    if (left && right) rules.push([left, right]);
  }

  return rules;
}

/**
 * Runs tasks a few at a time so a long collection does not open twenty
 * parallel connections at once.
 * @template T
 * @param {Array<() => Promise<T>>} tasks
 * @param {number} limit
 * @returns {Promise<T[]>}
 */
async function inBatches(tasks, limit) {
  const results = [];

  for (let index = 0; index < tasks.length; index += limit) {
    const batch = tasks.slice(index, index + limit);
    results.push(...(await Promise.all(batch.map((task) => task()))));
  }

  return results;
}

class LettoriaCollectionTabs extends HTMLElement {
  /** Every card the section rendered, in collection order. @type {HTMLElement[]} */
  #cards = [];

  /** Cards that qualify for grouping (see #qualifies). @type {HTMLElement[]} */
  #items = [];

  /** Group ids in tab order. @type {string[]} */
  #groups = [];

  /** Group id → how many of its cards are currently revealed. @type {Map<string, number>} */
  #revealed = new Map();

  /**
   * Card → its resolved group and normalized tag set, worked out once per
   * render. Filtering runs three passes over every card on each click, and
   * deriving these inline meant re-splitting the tag string and re-running
   * four regexes per tag, per card, per pass.
   * @type {WeakMap<HTMLElement, {group: string, tags: Set<string>}>}
   */
  #index = new WeakMap();

  /** Group id → total matching cards loaded. @type {Map<string, number>} */
  #counts = new Map();

  /** Stage tag → stage, so #matches is a lookup rather than a scan. @type {Map<string, object>} */
  #stageByTag = new Map();

  #active = ALL;

  async connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.#readSettings();
    this.#collectRefs();

    if (!this.grid) return;

    this.#cards = [...this.grid.querySelectorAll('[data-ctt-item]')];

    // Render tabs from the server-rendered first page straight away, so the
    // bar is usable before any follow-up page lands.
    this.#render();

    this.#bindEvents();

    const appended = await this.#loadRemainingPages();
    if (appended) this.#render();

    this.loading?.setAttribute('hidden', '');
  }

  #readSettings() {
    const { dataset } = this;

    this.sectionId = dataset.sectionId ?? '';
    this.lastPage = Number(dataset.lastPage) || 1;
    this.maxPages = Math.max(1, Number(dataset.maxPages) || 1);
    this.batch = Number(dataset.batch) || 24;
    this.showAll = dataset.showAll === 'true';
    this.allLabel = dataset.allLabel || 'All';
    this.stripLabel = dataset.stripLabel || '';

    this.labelMap = new Map(parseRules(dataset.labelMap));
    this.mergeMap = new Map(parseRules(dataset.mergeMap).map(([from, to]) => [normalizeKey(from), to]));
    this.stages = parseRules(dataset.tagStages).map(([label, tag]) => ({ label, tag, key: normalizeKey(tag) }));
    this.#stageByTag = new Map(this.stages.map((stage) => [stage.tag, stage]));

    this.tagMode = dataset.groupBy === 'tag' && this.stages.length > 0;
  }

  #collectRefs() {
    this.bar = this.querySelector('[data-ctt-tabs]');
    this.grid = this.querySelector('[data-ctt-grid]');
    this.footer = this.querySelector('[data-ctt-footer]');
    this.loadMore = this.querySelector('[data-ctt-load-more]');
    this.empty = this.querySelector('[data-ctt-empty]');
    this.count = this.querySelector('[data-ctt-count]');
    this.loading = this.querySelector('[data-ctt-loading]');
  }

  #bindEvents() {
    this.loadMore?.addEventListener('click', () => {
      this.#revealed.set(this.#active, this.#capFor(this.#active) + this.batch);
      this.#applyFilter();
      this.#updateFooter();
    });

    this.bar?.addEventListener('click', (event) => {
      const pill = /** @type {HTMLElement | null} */ (event.target)?.closest?.('[data-ctt-pill]');
      if (pill?.dataset.group) this.#activate(pill.dataset.group);
    });

    // Arrow keys move between tabs, as the tablist role promises. Focus follows
    // selection, which is the right choice here: switching is instant and has
    // no cost, so there is nothing to be gained by making it a two-step.
    this.bar?.addEventListener('keydown', (event) => {
      const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(event.key)) return;

      const pills = [...(this.bar?.querySelectorAll('[data-ctt-pill]') ?? [])];
      if (pills.length < 2) return;

      const current = pills.findIndex((pill) => pill.classList.contains('is-active'));
      let next = current;

      if (event.key === 'ArrowRight') next = (current + 1) % pills.length;
      if (event.key === 'ArrowLeft') next = (current - 1 + pills.length) % pills.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = pills.length - 1;

      const group = /** @type {HTMLElement} */ (pills[next])?.dataset.group;
      if (!group) return;

      event.preventDefault();
      this.#activate(group, { focus: true });
    });

    this.bar?.addEventListener('scroll', () => this.#updateScrollHint(), { passive: true });
    window.addEventListener('resize', () => this.#updateScrollHint(), { passive: true });
  }

  /* ── loading the rest of the collection ──────────────────────────── */

  /**
   * Fetches pages 2..N of this same section and appends their cards. Stops at
   * the ceiling the section passes down, so a very large collection costs a
   * bounded number of requests instead of one per fifty products. Returns
   * whether anything was added.
   * @returns {Promise<boolean>}
   */
  async #loadRemainingPages() {
    const target = Math.min(this.lastPage, this.maxPages);

    if (this.lastPage > this.maxPages) {
      console.warn(
        `[lettoria-collection-tabs] collection has ${this.lastPage} pages; loading the first ${this.maxPages} ` +
          `(${this.maxPages * 50} products). Tabs are built from those. Raise "Maximum products loaded" to cover the rest.`
      );
    }

    if (target < 2 || !this.sectionId) return false;

    this.loading?.removeAttribute('hidden');

    const tasks = [];
    for (let page = 2; page <= target; page++) {
      tasks.push(() => this.#fetchPage(page));
    }

    const pages = await inBatches(tasks, FETCH_CONCURRENCY);

    const fragment = document.createDocumentFragment();
    let added = 0;
    let failed = 0;

    // Concatenated in page order, so collection sort order survives — which
    // is what decides tab order further down.
    for (const cards of pages) {
      if (cards === null) {
        failed++;
        continue;
      }

      for (const card of cards) {
        fragment.append(card);
        this.#cards.push(card);
        added++;
      }
    }

    // A dropped page means missing products and possibly a missing tab. It
    // must not read as an empty stretch of the collection.
    if (failed) {
      console.warn(`[lettoria-collection-tabs] ${failed} of ${tasks.length} pages failed to load; tabs may be incomplete.`);
    }

    if (added) this.grid?.append(fragment);

    return added > 0;
  }

  /**
   * @param {number} page
   * @returns {Promise<HTMLElement[] | null>} null when the page could not be loaded
   */
  async #fetchPage(page) {
    // getSectionHTML writes section_id onto the URL it is handed, so it gets
    // a throwaway copy rather than a shared one.
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return [...parsed.querySelectorAll('[data-ctt-item]')];
    } catch (error) {
      console.warn(`[lettoria-collection-tabs] page ${page} failed to load`, error);
      return null;
    }
  }

  /* ── grouping ────────────────────────────────────────────────────── */

  /**
   * Tag mode needs at least one tag to place a card; type mode needs a type.
   * Cards that qualify for neither are hidden outright rather than piling up
   * under "All" — matching the old section, which dropped them before they
   * ever reached the grid.
   * @param {HTMLElement} card
   */
  #qualifies(card) {
    return this.tagMode ? Boolean(card.dataset.cttTags) : Boolean(card.dataset.cttType);
  }

  /**
   * Resolves a raw product type to the group it displays under.
   * @param {string | undefined} type
   */
  #groupOf(type) {
    if (!type) return '';
    return this.mergeMap.get(normalizeKey(type)) ?? type;
  }

  /**
   * @param {HTMLElement} card
   * @param {string} key - already-normalized tag
   */
  #hasTag(card, key) {
    return this.#index.get(card)?.tags.has(key) ?? false;
  }

  /**
   * @param {HTMLElement} card
   * @param {string} group
   */
  #matches(card, group) {
    if (group === ALL) return true;

    if (this.tagMode) {
      const stage = this.#stageByTag.get(group);
      return stage ? this.#hasTag(card, stage.key) : false;
    }

    return this.#index.get(card)?.group === group;
  }

  /**
   * Group ids in the order their first product appears in the collection.
   * @returns {string[]}
   */
  #buildGroups() {
    if (this.tagMode) {
      const seen = new Set();
      const ordered = [];

      for (const card of this.#items) {
        if (ordered.length === this.stages.length) break;

        // Every stage this product matches is registered, not just the first.
        // The old section stopped at the first hit, which meant a stage whose
        // products all carried an earlier stage's tag too never got a tab —
        // exactly what happens when a product runs in more than one wave.
        // Within a single product the stage order decides; across products it
        // is still collection order, so the reorder handle is unchanged.
        for (const stage of this.stages) {
          if (seen.has(stage.tag)) continue;
          if (!this.#hasTag(card, stage.key)) continue;

          seen.add(stage.tag);
          ordered.push(stage.tag);
        }
      }

      return ordered;
    }

    const seen = new Set();

    for (const card of this.#items) {
      const group = this.#index.get(card)?.group;
      if (group) seen.add(group);
    }

    return [...seen];
  }

  /**
   * @param {string} group
   * @returns {string}
   */
  #labelFor(group) {
    if (group === ALL) return this.allLabel;

    if (this.tagMode) {
      return this.stages.find((stage) => stage.tag === group)?.label ?? group;
    }

    const mapped = this.labelMap.get(group);
    if (mapped) return mapped;

    if (this.stripLabel) {
      const stripped = group.split(this.stripLabel).join('').replace(/\s+/g, ' ').trim();
      if (stripped) return stripped;
    }

    return group;
  }

  /* ── rendering ───────────────────────────────────────────────────── */

  #render() {
    this.#items = [];
    this.#counts = new Map();

    for (const card of this.#cards) {
      if (!this.#qualifies(card)) {
        card.setAttribute('hidden', '');
        continue;
      }

      if (!this.#index.has(card)) {
        this.#index.set(card, {
          group: this.#groupOf(card.dataset.cttType),
          tags: new Set((card.dataset.cttTags || '').split('|').filter(Boolean).map(normalizeKey)),
        });
      }

      this.#items.push(card);
    }

    if (!this.#items.length) {
      this.bar?.setAttribute('hidden', '');
      this.footer?.setAttribute('hidden', '');
      this.empty?.removeAttribute('hidden');
      this.#updateCount();
      return;
    }

    this.empty?.setAttribute('hidden', '');
    this.bar?.removeAttribute('hidden');

    this.#groups = this.#buildGroups();

    // One pass for every tab's total, instead of one pass per tab per click.
    this.#counts = new Map(this.#groups.map((group) => [group, 0]));
    for (const card of this.#items) {
      for (const group of this.#groups) {
        if (this.#matches(card, group)) this.#counts.set(group, this.#counts.get(group) + 1);
      }
    }
    this.#counts.set(ALL, this.#items.length);

    // A tab can vanish between renders (its only product lived on a page that
    // failed to load); fall back rather than filtering to nothing.
    if (this.#active !== ALL && !this.#groups.includes(this.#active)) {
      this.#active = this.showAll ? ALL : (this.#groups[0] ?? ALL);
    } else if (this.#active === ALL && !this.showAll) {
      this.#active = this.#groups[0] ?? ALL;
    }

    if (!this.#revealed.has(this.#active)) this.#revealed.set(this.#active, this.batch);

    this.#paintTabs();
    this.#applyFilter();
    this.#updateFooter();
    this.#updateCount();
    this.#updateScrollHint();
  }

  /**
   * Switches tab. Everything that has to move when the selection changes goes
   * through here, so the click and keyboard paths cannot drift apart.
   * @param {string} group
   * @param {{focus?: boolean}} [options]
   */
  #activate(group, options = {}) {
    if (group === this.#active) return;

    this.#active = group;
    if (!this.#revealed.has(group)) this.#revealed.set(group, this.batch);

    this.#paintTabs();
    this.#applyFilter();
    this.#updateFooter();
    this.#updateCount();
    this.#scrollActivePillIntoView();

    if (options.focus) {
      /** @type {HTMLElement | null} */ (this.bar?.querySelector('.lctabs__pill.is-active'))?.focus();
    }
  }

  #paintTabs() {
    if (!this.bar) return;

    const groups = this.showAll ? [ALL, ...this.#groups] : this.#groups;
    const panelId = this.grid?.id ?? '';

    this.bar.innerHTML = groups
      .map((group, index) => {
        const active = group === this.#active;

        return [
          '<button type="button" role="tab"',
          ` id="${this.#escape(this.#tabId(index))}"`,
          ` class="lctabs__pill${active ? ' is-active' : ''}"`,
          ` aria-selected="${active}"`,
          panelId ? ` aria-controls="${this.#escape(panelId)}"` : '',
          // Roving tabindex: the tablist is one stop in the page's tab order,
          // and the arrow keys move within it.
          ` tabindex="${active ? '0' : '-1'}"`,
          ' data-ctt-pill',
          ` data-group="${this.#escape(group)}">`,
          this.#escape(this.#labelFor(group)),
          '</button>',
        ].join('');
      })
      .join('');

    // The grid is the panel these tabs control; name it after the open tab.
    const activeIndex = groups.indexOf(this.#active);
    if (this.grid && activeIndex > -1) {
      this.grid.setAttribute('aria-labelledby', this.#tabId(activeIndex));
    }
  }

  /** @param {number} index */
  #tabId(index) {
    return `lctabs-tab-${this.sectionId || 'x'}-${index}`;
  }

  #applyFilter() {
    const cap = this.#capFor(this.#active);
    let shown = 0;

    for (const card of this.#items) {
      const visible = shown < cap && this.#matches(card, this.#active);
      card.toggleAttribute('hidden', !visible);
      if (visible) shown++;
    }
  }

  #updateFooter() {
    if (!this.footer) return;
    this.footer.toggleAttribute('hidden', this.#capFor(this.#active) >= this.#countFor(this.#active));
  }

  /**
   * The folio counts the OPEN TAB, not the collection. Sitting beside the
   * collection name, a bare number read as the collection's size — so the tab
   * it belongs to is named alongside it.
   */
  #updateCount() {
    if (!this.count) return;

    const total = this.#countFor(this.#active);
    const noun = total === 1 ? 'title' : 'titles';
    const label = this.#groups.length ? `${this.#labelFor(this.#active)} · ` : '';

    this.count.textContent = '';
    this.count.append(label, Object.assign(document.createElement('b'), { textContent: `${total} ${noun}` }));
  }

  #updateScrollHint() {
    if (!this.bar) return;
    this.bar.classList.toggle('is-scroll-end', this.bar.scrollLeft + this.bar.clientWidth >= this.bar.scrollWidth - 2);
  }

  #scrollActivePillIntoView() {
    const active = this.bar?.querySelector('.lctabs__pill.is-active');
    if (!this.bar || !(active instanceof HTMLElement)) return;

    const left = active.offsetLeft;
    const right = left + active.offsetWidth;
    const viewLeft = this.bar.scrollLeft;
    const viewRight = viewLeft + this.bar.clientWidth;

    if (left < viewLeft + 12) {
      this.bar.scrollTo({ left: Math.max(0, left - 12), behavior: 'smooth' });
    } else if (right > viewRight - 12) {
      this.bar.scrollTo({ left: right - this.bar.clientWidth + 12, behavior: 'smooth' });
    }
  }

  /** @param {string} group */
  #capFor(group) {
    return this.#revealed.get(group) ?? this.batch;
  }

  /** @param {string} group */
  #countFor(group) {
    return this.#counts.get(group) ?? 0;
  }

  /** @param {string} value */
  #escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('lettoria-collection-tabs')) {
  customElements.define('lettoria-collection-tabs', LettoriaCollectionTabs);
}
